/**
 * Pure builders shared by both overview normalizers (member and public), so the
 * two emit identical view-models for the same data. Deterministic, React-free,
 * and structurally typed so neither normalizer couples to the other.
 */

import { EventCategory } from '@orbit/api-types'
import { getInitials } from '@/utils/userNames'
import {
	addDays,
	formatCalendarDate,
	formatUnix,
	formatUtcInstant,
	formatWeekday,
	isValidCalendarDate,
	toCompact,
} from '@lib/datetime'
import { getDurationString, getFlightCode } from '@pages/Flights/flightHelpers'
import { pickSegmentRoute, SegmentRoutesInput, segmentRouteLabel } from '@pages/Itinerary/segmentDisplay'
import { OverviewDay, OverviewEvent, OverviewFlight, OverviewTravelSegment, OverviewTraveler } from './types'

/** How many day columns the itinerary preview shows. */
export const MAX_PREVIEW_DAYS = 2
/** Event cards per preview day column; the desktop tile clips to what fits. */
export const MAX_PREVIEW_EVENTS = 6
/** Event cap per day column below the bento collapse (content-sized layouts). */
export const MOBILE_PREVIEW_EVENTS = 3
/** How many idea thumbnails the ideas tile shows (a single row). */
export const MAX_PREVIEW_IDEAS = 3
/** How many stay rows the accommodations tile shows. */
export const MAX_PREVIEW_STAYS = 3
/** How many file thumbnails sit on the files tile. */
export const MAX_PREVIEW_FILES = 3

/**
 * Wire category value → human label, keyed by `EventCategory` so a new member
 * fails the build here. Unknown wire values fall back to "Other" at runtime.
 */
export const CATEGORY_LABELS: Record<EventCategory, string> = {
	FoodAndDrink: 'Food & Drink',
	SeeAndDo: 'See & Do',
	Nature: 'Nature',
	Shopping: 'Shopping',
	Accommodation: 'Accommodation',
	Transportation: 'Transportation',
	Event: 'Event',
	Other: 'Other',
}

export const categoryLabel = (category: string | null | undefined): string =>
	(category && CATEGORY_LABELS[category as EventCategory]) || CATEGORY_LABELS.Other

/** Minimum member shape the avatar stack + named balances need. */
export interface OverviewTravelerInput {
	id: number | string
	firstName: string
	lastName: string
	profilePicture?: string | null
}

/** Minimum event shape the itinerary preview needs. */
export interface OverviewPreviewEventInput {
	id: number | string
	name: string
	tripDayIndex: number
	eventIndex: number
	category?: string | null
	location?: { primaryText?: string | null } | null
	description?: string | null
	tripEventLinks?: readonly unknown[] | null
	fileIds?: readonly unknown[] | null
}

/** Minimum flight shape the flights preview needs (airport codes pre-resolved). */
export interface OverviewFlightInput {
	airlineIata: string
	flightNumber?: string | null
	originCode?: string | null
	destinationCode?: string | null
	/** Unix seconds (auto flights); `null` → fall back to the wall-clock string. */
	departureTime?: number | null
	/** Wall-clock `YYYY-MM-DD HH:MM:SS` (manual/public flights). */
	departureTimeStr?: string | null
	durationMinutes?: number | null
}

export const toTraveler = (member: OverviewTravelerInput): OverviewTraveler => ({
	id: String(member.id),
	name: `${member.firstName} ${member.lastName}`.trim(),
	initials: getInitials(member),
	avatarUrl: member.profilePicture ?? null,
})

const toOverviewEvent = (event: OverviewPreviewEventInput): OverviewEvent => ({
	id: String(event.id),
	title: event.name,
	categoryLabel: categoryLabel(event.category),
	hasLocation: !!event.location?.primaryText,
	hasNote: !!event.description?.trim(),
	hasLink: (event.tripEventLinks?.length ?? 0) > 0,
})

/** Whether an event falls on a day the trip actually has. */
export const isOnTripDay = (event: OverviewPreviewEventInput, dayCount: number): boolean =>
	event.tripDayIndex >= 0 && event.tripDayIndex < dayCount

/** Minimum travel-segment shape the itinerary preview needs. */
export interface OverviewSegmentInput extends SegmentRoutesInput {
	fromEventId: number | string
	toEventId: number | string
	tripDayIndex: number
}

/** The compact segment info for the rail, or `undefined` without route data. */
const toTravelAfter = (segment: OverviewSegmentInput | undefined): OverviewTravelSegment | undefined => {
	if (!segment) return undefined
	const route = pickSegmentRoute(segment)
	if (!route) return undefined
	return { mode: route.transportMode, label: segmentRouteLabel(route) }
}

/**
 * The first `MAX_PREVIEW_DAYS` trip days **that contain events**, each with its
 * first `MAX_PREVIEW_EVENTS` — so a column always carries a card even when all
 * events sit on later days. Falls back to the trip's first days when none do,
 * and returns `[]` without valid dates. Adjacent preview events carry the
 * matching segment's `travelAfter`, like the itinerary board's rail.
 */
export const buildPreviewDays = (
	startDate: string | null,
	dayCount: number,
	events: readonly OverviewPreviewEventInput[],
	segments: readonly OverviewSegmentInput[] = [],
): OverviewDay[] => {
	if (!isValidCalendarDate(startDate) || dayCount <= 0) return []

	const daysWithEvents = [
		...new Set(events.filter(event => isOnTripDay(event, dayCount)).map(event => event.tripDayIndex)),
	].sort((a, b) => a - b)

	const previewIndices =
		daysWithEvents.length > 0
			? daysWithEvents.slice(0, MAX_PREVIEW_DAYS)
			: Array.from({ length: Math.min(MAX_PREVIEW_DAYS, dayCount) }, (_, i) => i)

	const startCompact = toCompact(startDate)
	return previewIndices.map(dayIndex => {
		const date = addDays(startCompact, dayIndex)
		const dayInputs = events
			.filter(event => event.tripDayIndex === dayIndex)
			.sort((a, b) => a.eventIndex - b.eventIndex)
			.slice(0, MAX_PREVIEW_EVENTS)

		const dayEvents = dayInputs.map((event, index): OverviewEvent => {
			const next = dayInputs[index + 1]
			const segment = next
				? segments.find(
						s =>
							s.tripDayIndex === dayIndex &&
							String(s.fromEventId) === String(event.id) &&
							String(s.toEventId) === String(next.id),
					)
				: undefined
			return { ...toOverviewEvent(event), travelAfter: toTravelAfter(segment) }
		})

		return {
			id: String(dayIndex),
			dayName: formatWeekday(date, 'long'),
			dateLabel: formatCalendarDate(date, 'monthDay'),
			events: dayEvents,
		}
	})
}

/** Hero date range, e.g. "Apr 19 – May 4, 2026". Single-day collapses to one date. */
export const buildDateRange = (startDate: string | null, dayCount: number): string | null => {
	if (!isValidCalendarDate(startDate) || dayCount <= 0) return null

	const startCompact = toCompact(startDate)
	const endCompact = toCompact(addDays(startCompact, dayCount - 1))
	const endLabel = formatCalendarDate(endCompact, 'medium')

	if (dayCount === 1) return endLabel
	return `${formatCalendarDate(startCompact, 'monthDay')} – ${endLabel}`
}

/** Compact warm-chip range "Apr 19 → Apr 21" from two UTC instant strings. */
export const stayDateRange = (checkIn?: string | null, checkOut?: string | null): string | null => {
	if (!checkIn || !checkOut) return null
	return `${formatUtcInstant(checkIn, 'monthDay')} → ${formatUtcInstant(checkOut, 'monthDay')}`
}

const flightDayLabel = (flight: OverviewFlightInput): string => {
	if (flight.departureTime != null) return formatUnix(flight.departureTime, 'monthDay') ?? ''
	// Wall-clock manual flight: take the calendar day (never shifted for the viewer).
	return formatCalendarDate(flight.departureTimeStr?.slice(0, 10), 'monthDay')
}

export const toOverviewFlight = (flight: OverviewFlightInput): OverviewFlight => {
	const dayLabel = flightDayLabel(flight)
	const dur = getDurationString(flight.durationMinutes)
	const metaLabel = [dayLabel, dur].filter(Boolean).join(' · ')
	return {
		originCode: flight.originCode || '—',
		destinationCode: flight.destinationCode || '—',
		flightCode: getFlightCode(flight),
		metaLabel,
	}
}

/** Sum monetary rows in the given currency only (raw hundredths). */
export const sumInCurrency = (
	rows: readonly { amount: number; currencyCode: string }[],
	currencyCode: string,
): number => rows.filter(r => r.currencyCode === currencyCode).reduce((sum, r) => sum + r.amount, 0)
